import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { TaskRequest, TaskStatus } from "@models/task-request";
import { Router } from "@angular/router";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { PaginatorState } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  startWith,
  Subject,
  switchMap,
} from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { PaginatorReportComponent } from "../../shared/components/paginator-report/paginator-report.component";
import { AdminTasksService } from "@services/admin-tasks.service";
import { StatusSummaryCardComponent } from "../../shared/components/cards/status-summary-card/status-summary-card.component";
import { SolicitationsTableComponent } from "./solicitations-table/solicitations-table.component";

@Component({
  selector: "app-solicitations-page",
  imports: [
    CommonModule,
    AppLayoutComponent,
    PageHeaderComponent,
    PaginatorReportComponent,
    SolicitationsTableComponent,
    StatusSummaryCardComponent,
    ButtonComponent,
    ReactiveFormsModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    SkeletonModule,
  ],
  templateUrl: "./solicitations-page.component.html",
  styleUrl: "./solicitations-page.component.scss",
})
export class SolicitationsPageComponent {
  private readonly _adminTasksService = inject(AdminTasksService);
  private readonly _router = inject(Router);

  tableFilter = new FormControl("", { nonNullable: true });

  private readonly page$ = new Subject<PaginatorState>();
  private readonly statusFilter$ = new BehaviorSubject<TaskStatus | null>(null);

  activeStatusFilter: TaskStatus | null = null;

  tasks$ = combineLatest([
    this.page$.pipe(startWith(null)),
    this.tableFilter.valueChanges.pipe(startWith(""), debounceTime(500)),
    this.statusFilter$.pipe(startWith(null)),
  ]).pipe(
    switchMap(([page, search, status]) =>
      this._adminTasksService.getTasks(page, search, status),
    ),
  );

  handlePageChange(page: PaginatorState) {
    this.page$.next(page);
  }

  get hasActiveFilters(): boolean {
    return (
      this.activeStatusFilter !== null ||
      this.tableFilter.value.trim().length > 0
    );
  }

  handleStatusFilter(status: TaskStatus | null) {
    if (this.activeStatusFilter === status) {
      this.activeStatusFilter = null;
      this.statusFilter$.next(null);
    } else {
      this.activeStatusFilter = status;
      this.statusFilter$.next(status);
    }
  }

  clearFilters() {
    this.tableFilter.setValue("");
    this.activeStatusFilter = null;
    this.statusFilter$.next(null);
  }

  summary$ = this._adminTasksService.getSummary();

  taskStatusEnum = TaskStatus;

  handleViewDetails(task: TaskRequest) {
    this._router.navigate(["/solicitations", task.id]);
  }
}
